import './applicantEmploymentStatusPage.scss';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { ThemeProvider } from '@mui/material/styles';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import { NO, YES, YesOrNo } from '../../../store/applicantStore/types/YesOrNo';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    isCurrentlyEmployed,
    isSyncingIncome,
    clearCurrentEmployment,
    setIsCurrentlyEmployed,
  } = useApplicantStore();

  const {
    formState: { isDirty },
    handleSubmit,
    register,
    watch,
  } = useForm({
    defaultValues: {
      isCurrentlyEmployedValue: isCurrentlyEmployed,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (event: RadioOptionClickEvent<YesOrNo>): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const isCurrentlyEmployedValue = event.target.value;

    setIsInitialSelection(
      isEmpty(isCurrentlyEmployed) && isValidClickEvent(event),
    );

    setIsCurrentlyEmployed(isCurrentlyEmployedValue);
  };

  const formSubmit = (values: FieldValues): void => {
    const { isCurrentlyEmployedValue } = values;

    if (isCurrentlyEmployedValue) {
      if (isDirty) {
        clearCurrentEmployment();
      }

      navigate(next());
    }
  };

  useEffect((): void => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-employment-status-page" sx={theme.pageLayout}>
        <Header
          progress={progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.applicantEmploymentStatus.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantEmploymentStatus.form.h1}>
                {t('ApplicantEmploymentStatusPage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantEmploymentStatus.form.h2}>
                {t('ApplicantEmploymentStatusPage.subtitle')}
              </h2>

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  labelPlacement="below"
                  name="isCurrentlyEmployedValue"
                  options={[
                    {
                      label: t('ApplicantEmploymentStatusPage.yes'),
                      value: YES,
                      testId:
                        locator.pages.applicantEmploymentStatus.form
                          .radioOption1,
                    },
                    {
                      label: t('ApplicantEmploymentStatusPage.no'),
                      value: NO,
                      testId:
                        locator.pages.applicantEmploymentStatus.form
                          .radioOption2,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.applicantEmploymentStatus.form.radioGroupRoot
                  }
                  variant="inline"
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  color="primary"
                  data-testid={
                    locator.pages.applicantEmploymentStatus.form.submitButton
                  }
                  disabled={isEmpty(isCurrentlyEmployed)}
                  loading={isSyncingIncome}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('ApplicantEmploymentStatusPage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={locator.pages.applicantEmploymentStatus.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantEmploymentStatusPage';
